/**
 * Transfer Portal tab — shows open transfers from real Medplum ServiceRequests.
 *
 * Queries ServiceRequests with tracker stage `transfer` or `consultation`,
 * hydrates Patient + Coverage + transfer QR for each row.
 *
 * PRD: docs/prd/Tasks/PRD_TRANSFER_REQUESTS_FHIR_MAPPING.md § Transfer Portal
 */

import {
  ActionIcon,
  Badge,
  Group,
  Loader,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconInfoCircle } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { FilterMultiSelect } from '@/components/FilterMultiSelect';
import type {
  Coverage,
  Patient,
  QuestionnaireResponse,
  ServiceRequest,
} from '@medplum/fhirtypes';
import {
  TRACKER_STAGE_TAG_SYSTEM,
  readTrackerStage,
  readEpisodeOfCareId,
} from '../../../lib/serviceRequestFhirConstants';
import { getTransferQRForEpisode } from '../../../lib/transferService';
import { TransferDetailModal } from '../components/TransferDetailModal';
import { CaseloadTabPanelLayout } from '../components/CaseloadTabPanelLayout';

const PAYMENT_TYPE_TOOLTIP =
  'Clients who do not match your accepted payment types will not appear on this table.';

interface TransferRow {
  sr: ServiceRequest;
  patientName: string;
  patientId: string;
  submissionDate: string;
  paymentType: string;
  fromProvider: string;
  stage: string;
  episodeId: string | undefined;
  qr?: QuestionnaireResponse;
  episodeDisplay?: string;
}

interface TransfersTabProps {
  /** Bumps when the caseload page or tab is revisited — triggers a fresh Medplum fetch. */
  refreshKey?: number;
}

export function TransfersTab({ refreshKey }: TransfersTabProps): JSX.Element {
  const medplum = useMedplum();
  const [statusFilter, setStatusFilter] = useState<string[]>(['transfer']);

  const [rows, setRows] = useState<TransferRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState<TransferRow | null>(null);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      medplum.invalidateSearches('ServiceRequest');

      const wantsTransfer = statusFilter.includes('transfer');
      const wantsConsultation = statusFilter.includes('consultation');
      if (statusFilter.length === 0) {
        setRows([]);
        return;
      }
      const tagValues =
        wantsConsultation && !wantsTransfer
          ? `${TRACKER_STAGE_TAG_SYSTEM}|consultation`
          : wantsTransfer && !wantsConsultation
            ? `${TRACKER_STAGE_TAG_SYSTEM}|transfer`
            : `${TRACKER_STAGE_TAG_SYSTEM}|transfer,${TRACKER_STAGE_TAG_SYSTEM}|consultation`;

      const srs = await medplum.searchResources('ServiceRequest', {
        _tag: tagValues,
        _count: '200',
        _sort: '-_lastUpdated',
      });

      const patientIds = new Set<string>();
      for (const sr of srs) {
        const pid = sr.subject?.reference?.replace('Patient/', '');
        if (pid) patientIds.add(pid);
      }

      const patients = patientIds.size > 0
        ? await medplum.searchResources('Patient', {
            _id: [...patientIds].join(','),
            _count: String(patientIds.size),
          })
        : [];
      const patientsById = new Map<string, Patient>();
      for (const p of patients) if (p.id) patientsById.set(p.id, p);

      const coverages = patientIds.size > 0
        ? await medplum.searchResources('Coverage', {
            beneficiary: [...patientIds].map((id) => `Patient/${id}`).join(','),
            _count: '200',
          }).catch(() => [] as Coverage[])
        : [];
      const coverageByPatient = new Map<string, string>();
      for (const c of coverages) {
        const ref = c.beneficiary?.reference;
        const pid = ref?.replace('Patient/', '');
        if (pid && !coverageByPatient.has(pid)) {
          coverageByPatient.set(pid, c.payor?.[0]?.display ?? 'Insurance');
        }
      }

      const transferRows: TransferRow[] = [];
      for (const sr of srs) {
        const pid = sr.subject?.reference?.replace('Patient/', '') ?? '';
        const patient = patientsById.get(pid);
        const stage = readTrackerStage(sr.meta) ?? 'transfer';
        const episodeId = readEpisodeOfCareId(sr);

        let qr: QuestionnaireResponse | undefined;
        if (episodeId) {
          qr = await getTransferQRForEpisode(medplum, episodeId, { patientId: pid }).catch(
            () => undefined,
          );
        }

        const patientName = patient
          ? [
              ...(patient.name?.[0]?.given ?? []),
              patient.name?.[0]?.family,
            ]
              .filter(Boolean)
              .join(' ') || sr.subject?.display || 'Client'
          : sr.subject?.display ?? 'Client';

        transferRows.push({
          sr,
          patientName,
          patientId: pid,
          submissionDate: qr?.authored?.slice(0, 10) ?? sr.authoredOn?.slice(0, 10) ?? '',
          paymentType: coverageByPatient.get(pid) ?? 'Self-Pay',
          fromProvider: qr?.author?.display ?? '—',
          stage,
          episodeId,
          qr,
          episodeDisplay: sr.code?.coding?.[0]?.display,
        });
      }

      setRows(transferRows);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, statusFilter]);

  useEffect(() => {
    void fetchTransfers();
  }, [fetchTransfers, refreshKey]);

  const columns = useMemo(
    (): UnifyTableColumn<TransferRow>[] => [
      {
        key: 'client',
        label: 'Client Name',
        sortable: true,
        getValue: (row) => row.patientName,
        render: (row) => <Text size="sm">{row.patientName}</Text>,
      },
      {
        key: 'submissionDate',
        label: 'Submission Date',
        sortable: true,
        getValue: (row) => row.submissionDate,
        render: (row) => <Text size="sm">{row.submissionDate}</Text>,
      },
      {
        key: 'paymentType',
        label: 'Payment Type',
        sortable: true,
        getValue: (row) => row.paymentType,
        renderHeader: () => (
          <Group gap={4} wrap="nowrap">
            Payment Type
            <Tooltip label={PAYMENT_TYPE_TOOLTIP}>
              <ActionIcon size="xs" variant="subtle" color="gray">
                <IconInfoCircle size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ),
        render: (row) => <Text size="sm">{row.paymentType}</Text>,
      },
      {
        key: 'provider',
        label: 'Provider (from)',
        sortable: true,
        getValue: (row) => row.fromProvider,
        render: (row) => <Text size="sm">{row.fromProvider}</Text>,
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        getValue: (row) => row.stage,
        render: (row) => (
          <Badge
            variant="light"
            color={row.stage === 'transfer' ? 'yellow' : 'blue'}
            size="sm"
          >
            {row.stage === 'transfer' ? 'Pending Transfer' : 'Pending Consultation'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const handleRowClick = (row: TransferRow) => {
    setSelectedRow(row);
    openDetail();
  };

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  return (
    <>
      <CaseloadTabPanelLayout
        filters={
          <FilterMultiSelect
            allLabel="All Open Transfers"
            data={[
              { value: 'transfer', label: 'Pending Transfer' },
              { value: 'consultation', label: 'Pending Consultation' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        }
      >
        <UnifyTable<TransferRow>
          withPaper={false}
          searchable={false}
          columns={columns}
          data={rows}
          getRowKey={(row) => row.sr.id ?? row.patientId}
          sortable
          pagination
          defaultRowsPerPage={50}
          emptyMessage="No transfer requests found"
          onRowClick={handleRowClick}
          tableProps={{ highlightOnHover: true }}
        />
      </CaseloadTabPanelLayout>

      <TransferDetailModal
        opened={detailOpened}
        onClose={closeDetail}
        clientName={selectedRow?.patientName}
        episodeDisplay={selectedRow?.episodeDisplay}
        status={selectedRow?.stage}
        fromProvider={selectedRow?.fromProvider}
        paymentType={selectedRow?.paymentType}
        submissionDate={selectedRow?.submissionDate}
        questionnaireResponse={selectedRow?.qr}
        serviceRequest={selectedRow?.sr}
      />
    </>
  );
}
